import { Printer, Copy, CalendarDays, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmt, fmtRupiah, PRICE_ITEMS } from "@/lib/feedModels";

export default function ResultBreakdown({ result, cost, prices, setPrices, onPrint, onCopy, showCost = true }) {
  const updatePrice = (id, value) =>
    setPrices((prev) => ({ ...prev, [id]: value }));

  return (
    <div
      data-testid="result-breakdown"
      className="lg:col-span-7 bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-[#E0A988]/40 space-y-7"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg md:text-xl font-bold tracking-tight text-[#8A5230]">
            Rincian Formula Pakan
          </h2>
          <p className="text-xs md:text-sm text-[#A06E4D] mt-1">
            {result
              ? `${result.model.label} — Comboran : ${result.model.type === "silase" ? "Silase" : "Onggok"} (${result.model.comboran * 100}% : ${result.model.hijauan * 100}%)`
              : "Hasil perhitungan akan tampil di sini."}
          </p>
        </div>
        {result && (
          <div className="flex gap-2">
            <Button
              data-testid="btn-export-pdf"
              onClick={onPrint}
              variant="outline"
              className="rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
            >
              <Printer className="w-4 h-4 mr-2" />
              Cetak PDF
            </Button>
            <Button
              data-testid="btn-copy-result"
              onClick={onCopy}
              variant="outline"
              className="rounded-full border-[#E0A988]/70 text-[#A06E4D] hover:bg-[#F7E5D2]"
            >
              <Copy className="w-4 h-4 mr-2" />
              Salin
            </Button>
          </div>
        )}
      </div>

      {!result ? (
        <div
          data-testid="result-empty-state"
          className="rounded-xl border border-dashed border-[#E0A988]/60 bg-[#FBF3E8] p-10 text-center text-sm text-[#C99A6A]"
        >
          Masukkan total bobot sapi untuk melihat rincian pakan.
        </div>
      ) : (
        <>
          <div>
            <div className="flex justify-between text-xs font-semibold text-[#A06E4D] mb-2">
              <span className="text-[#8A5230]">
                Comboran {result.model.comboran * 100}%
              </span>
              <span className="text-[#C99A6A]">
                {result.model.type === "silase" ? "Silase" : "Onggok"} {result.model.hijauan * 100}%
              </span>
            </div>
            <div
              data-testid="ratio-bar"
              className="flex h-4 w-full overflow-hidden rounded-full bg-[#F0E3D2]"
            >
              <div
                className="bg-[#A06E4D] transition-[width] duration-500"
                style={{ width: `${result.model.comboran * 100}%` }}
              />
              <div
                className="bg-[#E0A988] transition-[width] duration-500"
                style={{ width: `${result.model.hijauan * 100}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div
              data-testid="comboran-summary"
              className="rounded-xl bg-[#FBEAD6] border border-[#E0A988]/50 p-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#8A5230]">
                Kebutuhan Comboran
              </p>
              <p className="text-2xl md:text-3xl font-mono-num font-bold text-[#7A4A28] mt-1">
                {fmt(result.comboranKg)}
                <span className="text-sm font-sans font-semibold text-[#A06E4D] ml-1">kg</span>
              </p>
            </div>
            <div
              data-testid="hijauan-summary"
              className="rounded-xl bg-[#F7E5D2] border border-[#E0A988]/50 p-5"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-[#A06E4D]">
                {result.model.type === "silase" ? "Kebutuhan Silase" : "Onggok (Hijauan)"}
              </p>
              <p className="text-2xl md:text-3xl font-mono-num font-bold text-[#7A4A28] mt-1">
                {fmt(result.model.type === "silase" ? result.silaseKg : result.hijauanKg)}
                <span className="text-sm font-sans font-semibold text-[#A06E4D] ml-1">kg</span>
              </p>
              {result.model.type !== "silase" && (
                <p data-testid="onggok-caption" className="text-[11px] text-[#A06E4D] mt-1">
                  {Math.round(result.model.hijauan * 100)}% dari total kebutuhan pakan
                </p>
              )}
              {result.model.type === "silase" && (
                <p data-testid="silase-conversion-note" className="text-[11px] text-[#A06E4D] mt-1">
                  dari {fmt(result.hijauanKg)} kg hijauan segar (konversi {Math.round((result.model.silaseKonversi ?? 0.5) * 100)}%)
                </p>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div data-testid="hijauan-pagi" className="rounded-lg bg-white/70 border border-[#E0A988]/40 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#B87148]">
                    Pagi (30%)
                  </p>
                  <p className="text-base font-mono-num font-bold text-[#7A4A28]">
                    {fmt(result.hijauanPagiKg)}
                    <span className="text-xs font-sans font-semibold text-[#A06E4D] ml-1">kg</span>
                  </p>
                </div>
                <div data-testid="hijauan-sore" className="rounded-lg bg-white/70 border border-[#E0A988]/40 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#B87148]">
                    Sore (70%)
                  </p>
                  <p className="text-base font-mono-num font-bold text-[#7A4A28]">
                    {fmt(result.hijauanSoreKg)}
                    <span className="text-xs font-sans font-semibold text-[#A06E4D] ml-1">kg</span>
                  </p>
                </div>
              </div>
              {result.model.type === "silase" && (
                <p data-testid="silase-polar-note" className="mt-2 text-[11px] font-medium text-[#8A5230]">
                  Catatan: polar fermentasi {fmt(result.hijauanKg * 0.03)}-{fmt(result.hijauanKg * 0.05)} kg (3-5% dari hijauan segar)
                </p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-[#7A4A28] mb-3">
              Rincian Comboran ({fmt(result.comboranKg)} kg/hari)
            </h3>
            <div data-testid="ingredient-list" className="divide-y divide-[#F0E3D2] rounded-xl border border-[#F0E3D2]">
              {result.ingredients.map((ing) => (
                <div
                  key={ing.id}
                  data-testid={`ingredient-${ing.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-[#FBF3E8] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: ing.color }}
                    />
                    <span className="text-sm font-medium text-[#7A4A28]">
                      {ing.label}
                    </span>
                    <span className="text-xs font-mono-num text-[#C99A6A]">
                      {ing.pct * 100}%
                    </span>
                  </div>
                  <span className="text-sm md:text-base font-mono-num font-bold text-[#7A4A28]">
                    {fmt(ing.kg)} kg
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            data-testid="monthly-estimate"
            className="rounded-xl border border-[#E0A988]/50 bg-[#FBF3E8] p-5"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#A06E4D] flex items-center justify-center">
                <CalendarDays className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#7A4A28]">
                  Estimasi Kebutuhan Bulanan
                </h3>
                <p className="text-xs text-[#A06E4D]">
                  Perencanaan stok bahan untuk 30 hari
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg bg-white border border-[#E0A988]/40 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#A06E4D] font-semibold">Total Pakan</p>
                <p data-testid="monthly-total-feed" className="text-lg md:text-xl font-mono-num font-bold text-[#7A4A28]">
                  {fmt(result.monthly.totalFeed)}
                </p>
                <p className="text-[10px] text-[#C99A6A]">kg / bulan</p>
              </div>
              <div className="rounded-lg bg-white border border-[#E0A988]/40 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#A06E4D] font-semibold">Comboran</p>
                <p data-testid="monthly-comboran" className="text-lg md:text-xl font-mono-num font-bold text-[#7A4A28]">
                  {fmt(result.monthly.comboranKg)}
                </p>
                <p className="text-[10px] text-[#C99A6A]">kg / bulan</p>
              </div>
              <div className="rounded-lg bg-white border border-[#E0A988]/40 p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#A06E4D] font-semibold">
                  {result.model.type === "silase" ? "Silase" : "Onggok"}
                </p>
                <p data-testid="monthly-hijauan" className="text-lg md:text-xl font-mono-num font-bold text-[#7A4A28]">
                  {fmt(result.model.type === "silase" && result.monthly.silaseKg != null ? result.monthly.silaseKg : result.monthly.hijauanKg)}
                </p>
                <p className="text-[10px] text-[#C99A6A]">kg / bulan</p>
              </div>
            </div>

            <p className="text-xs font-semibold text-[#8A5230] mb-2">Kebutuhan Bahan Comboran per Bulan</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {result.ingredients.map((ing) => (
                <div
                  key={ing.id}
                  data-testid={`monthly-ingredient-${ing.id}`}
                  className="rounded-lg bg-white border border-[#E0A988]/40 p-2 text-center"
                >
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ing.color }} />
                    <span className="text-[11px] font-medium text-[#7A4A28]">{ing.label}</span>
                  </div>
                  <p className="text-sm font-mono-num font-bold text-[#7A4A28]">{fmt(ing.monthlyKg)}</p>
                  <p className="text-[10px] text-[#C99A6A]">kg</p>
                </div>
              ))}
            </div>
          </div>

          <div
            data-testid="cost-estimate"
            className={`rounded-xl border border-[#E0A988]/50 bg-white p-5 ${showCost ? "" : "hidden"}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#8A5230] flex items-center justify-center">
                <Wallet className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#7A4A28]">
                  Estimasi Biaya Pakan
                </h3>
                <p className="text-xs text-[#A06E4D]">
                  Masukkan harga per kg tiap bahan
                </p>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              {PRICE_ITEMS.filter((item) => result.model.type === "silase" || item.id !== "hijauan").map((item) => {
                const line = cost?.items.find((i) => i.id === item.id);
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-3"
                    data-testid={`cost-row-${item.id}`}
                  >
                    <span className="w-24 shrink-0 text-sm font-medium text-[#7A4A28]">
                      {item.label}
                    </span>
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#C99A6A]">
                        Rp
                      </span>
                      <Input
                        data-testid={`price-input-${item.id}`}
                        type="number"
                        min="0"
                        step="any"
                        placeholder="0"
                        value={prices[item.id]}
                        onChange={(e) => updatePrice(item.id, e.target.value)}
                        className="h-10 pl-9 text-sm font-mono-num bg-[#FBF3E8] border-[#E0A988]/50 focus-visible:ring-[#A06E4D]"
                      />
                    </div>
                    <span
                      data-testid={`cost-monthly-${item.id}`}
                      className="w-28 shrink-0 text-right text-sm font-mono-num font-semibold text-[#8A5230]"
                    >
                      {line ? fmtRupiah(line.monthlyCost) : "Rp 0"}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-[#FBEAD6] border border-[#E0A988]/50 p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#A06E4D] font-semibold">Biaya per Hari</p>
                <p data-testid="cost-daily-total" className="text-lg md:text-xl font-mono-num font-bold text-[#7A4A28]">
                  {fmtRupiah(cost?.dailyCost)}
                </p>
              </div>
              <div className="rounded-lg bg-[#8A5230] text-white p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#F7E5D2] font-semibold">Biaya per Bulan</p>
                <p data-testid="cost-monthly-total" className="text-lg md:text-xl font-mono-num font-bold">
                  {fmtRupiah(cost?.monthlyCost)}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
